/**
 * Google ID token refresh (Task 4092 step 4).
 *
 * Google ID tokens expire in ~1 hour. Unlike the Firebase SDK, Google Identity
 * Services does not silently renew in the background — the token must be
 * re-requested. Without this a support agent is signed out mid-shift.
 *
 * Refresh is driven by 401s rather than a timer:
 *
 *   - A timer has to guess the expiry and races clock skew on both ends.
 *   - A 401 is the authoritative signal that the gateway rejected the token,
 *     whatever the reason (expired, revoked, key rotation).
 *
 * The cost is one failed request per expiry, which the caller then retries
 * transparently. That is the trade the retry-on-401 requirement already implies.
 *
 * Desktop is unaffected: the Rust layer holds a refresh token in the Keychain
 * and mints fresh ID tokens itself, so `refreshCredential` is a no-op there.
 */

import { api } from '@/lib/api'
import { useGoogleAuthStore } from '../stores/google-auth-store'
import { IS_TAURI } from './staff-session'
import { requestIdToken } from './gis'

/**
 * In-flight refresh, shared by every caller that 401s at the same time.
 *
 * Without this, a page issuing six parallel requests on a stale token would
 * fire six concurrent GIS prompts. They all want the same new token, so they
 * await one request.
 */
let inFlight: Promise<string | null> | null = null

/**
 * Incremented on sign-out to invalidate any refresh already in flight.
 *
 * Dropping the refresher at sign-out stops a NEW refresh from starting, but not
 * one already awaiting GIS (PR #109 N-A). That closure re-arms the client and
 * flips the store back to `authenticated` when it resolves — signing the
 * operator back in after they explicitly signed out, with a fresh one-hour
 * token. The GIS round-trip is a real network call, so the window is wide
 * enough to hit in practice.
 */
let generation = 0

/**
 * Obtain a fresh credential and reconfigure the API client.
 *
 * Returns the new token, or null when refresh is impossible or not applicable
 * (desktop, or no silent session available). A null return means the caller
 * should surface the original 401 rather than retry.
 */
export function refreshCredential(): Promise<string | null> {
  // Desktop mints its own tokens per request via the Rust layer; there is
  // nothing for the frontend to refresh.
  if (IS_TAURI) return Promise.resolve(null)

  if (inFlight) return inFlight

  const startedAt = generation

  inFlight = (async () => {
    try {
      // Silent: the operator has an active Google session and has already
      // consented, so this should not prompt. If it would prompt, we fail and
      // let the 401 surface — interrupting someone mid-task with an account
      // chooser is worse than one visible error.
      const idToken = await requestIdToken()

      // Signed out while we were away — discard the token rather than re-arming
      // a client the operator has just torn down.
      if (startedAt !== generation) {
        return null
      }

      // updateCredential, not reconfigure: refreshing a token must not abort
      // the operator's active SSE streams the way an environment switch does.
      api.updateCredential(idToken)
      useGoogleAuthStore.setState({ idToken, status: 'authenticated' })
      return idToken
    } catch (err) {
      // A refusal because the sign-in gate owns the flow is a deliberate yield,
      // not a failure (PR #111 N-new-2). Writing "your session expired" for it
      // tells an operator who has never signed in this session that their
      // session expired — noise that reads as a bug.
      const message = err instanceof Error ? err.message : String(err)
      if (message.includes('gate_active')) {
        return null
      }

      // Refresh failed — the session is genuinely gone. Drop back to the gate
      // so the operator can sign in again rather than seeing repeated errors.
      // Skip if they already signed out: overwriting `error` there would show a
      // spurious "session expired" on a deliberate sign-out.
      if (startedAt === generation) {
        useGoogleAuthStore.setState({
          status: 'unauthenticated',
          idToken: null,
          error: 'Your session expired. Sign in again to continue.',
        })
      }
      return null
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Invalidate any in-flight refresh, so a token that arrives after sign-out is
 * discarded instead of re-arming the client (PR #109 N-A).
 */
export function cancelRefreshForSignOut(): void {
  generation += 1
  inFlight = null
}

/** Test seam: drop any shared in-flight refresh and reset the generation. */
export function resetRefreshStateForTests(): void {
  inFlight = null
  generation = 0
}
